import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Form } from 'react-bootstrap'

import { DashboardLayout } from '../../Components/Layout/DashboardLayout'
import CustomButton from '../../Components/CustomButton'
import CustomInput from '../../Components/CustomInput'
import { useCreateAdminMutation } from '../../Redux/Apis/Admin'
import { useGetNeighborhoodsQuery } from '../../Redux/Apis/Neighborhood'

import './style.css'

const CreateAdmin = () => {
  const navigate = useNavigate()
  const [createAdmin, { isLoading }] = useCreateAdminMutation()
  const { data: neighborhoodsData } = useGetNeighborhoodsQuery()

  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'neighborhood_admin',
    assignedNeighborhoods: [],
    defaultNeighborhood: '',
    permissions: {
      manageNeighborhoods: false,
      manageAdmins: false,
      accessAllNeighborhoods: false,
      deleteUsers: false,
      manageSubscriptions: false
    }
  })

  const handleChange = (field, value) => {
    if (field.startsWith('permissions.')) {
      const permKey = field.split('.')[1]
      setFormData(prev => ({
        ...prev,
        permissions: {
          ...prev.permissions,
          [permKey]: value
        }
      }))
    } else {
      setFormData(prev => ({ ...prev, [field]: value }))
    }
  }

  const handleRoleChange = (role) => {
    setFormData(prev => ({
      ...prev,
      role,
      permissions: role === 'super_admin' ? {
        manageNeighborhoods: true,
        manageAdmins: true,
        accessAllNeighborhoods: true,
        deleteUsers: true,
        manageSubscriptions: true
      } : {
        manageNeighborhoods: false,
        manageAdmins: false,
        accessAllNeighborhoods: false,
        deleteUsers: false,
        manageSubscriptions: false
      }
    }))
  }

  const handleNeighborhoodToggle = (neighborhoodId) => {
    setFormData(prev => {
      const assigned = prev.assignedNeighborhoods.includes(neighborhoodId)
        ? prev.assignedNeighborhoods.filter(id => id !== neighborhoodId)
        : [...prev.assignedNeighborhoods, neighborhoodId]

      return { ...prev, assignedNeighborhoods: assigned }
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (formData.role === 'neighborhood_admin' && formData.assignedNeighborhoods.length === 0) {
      toast.error('Neighborhood admin must have at least one assigned neighborhood')
      return
    }

    try {
      await createAdmin(formData).unwrap()
      toast.success('Admin created and invite email sent')
      navigate('/admins')
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to create admin')
    }
  }

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="row mb-3">
          <div className="col-12">
            <div className="dashCard create-admin-container">
              <div className="admin-create-intro">
                <div>
                  <p className="admin-create-kicker">Admin Console</p>
                  <h2 className="mainTitle mb-2">Create New Admin</h2>
                  <p className="secondaryText mb-0">
                    Set access, assign neighborhoods, and send a clean first-login invite without manually sharing credentials.
                  </p>
                </div>
                <CustomButton
                  variant="secondaryButton"
                  onClick={() => navigate('/admins')}
                >
                  Back to List
                </CustomButton>
              </div>

              <Form onSubmit={handleSubmit}>
                <div className="row g-4">
                  <div className="col-xl-8">
                    <div className="admin-create-panel">
                      <div className="section-divider">
                        <h5 className="section-title">Basic Information</h5>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <CustomInput
                            label="Name"
                            id="name"
                            type="text"
                            placeholder="John Doe"
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            required
                          />
                        </div>

                        <div className="col-md-6">
                          <CustomInput
                            label="Email"
                            id="email"
                            type="email"
                            placeholder="admin@theturnat.co"
                            value={formData.email}
                            onChange={(e) => handleChange('email', e.target.value)}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="admin-create-panel">
                      <div className="section-divider">
                        <h5 className="section-title">Role & Access</h5>
                      </div>

                      <label className="mainLabel">Select Role</label>
                      <div className="role-selection-container mt-2">
                        <label className={`role-option ${formData.role === 'super_admin' ? 'selected' : ''}`}>
                          <input
                            type="radio"
                            name="role"
                            checked={formData.role === 'super_admin'}
                            onChange={() => handleRoleChange('super_admin')}
                          />
                          <div className="role-label">
                            <strong>Super Admin</strong>
                            <p className="secondaryText mb-0 mt-1">Full system access across every neighborhood and admin tool.</p>
                          </div>
                        </label>

                        <label className={`role-option ${formData.role === 'neighborhood_admin' ? 'selected' : ''}`}>
                          <input
                            type="radio"
                            name="role"
                            checked={formData.role === 'neighborhood_admin'}
                            onChange={() => handleRoleChange('neighborhood_admin')}
                          />
                          <div className="role-label">
                            <strong>Neighborhood Admin</strong>
                            <p className="secondaryText mb-0 mt-1">Scoped access for a specific set of neighborhoods.</p>
                          </div>
                        </label>
                      </div>

                      {formData.role === 'neighborhood_admin' && (
                        <>
                          <div className="section-divider">
                            <h5 className="section-title">Assign Neighborhoods</h5>
                          </div>
                          <p className="secondaryText mb-3">Select the neighborhoods this admin can open in the console.</p>

                          <div className="neighborhood-checkboxes">
                            {(neighborhoodsData?.data || []).map(neighborhood => (
                              <div className="neighborhood-checkbox-item" key={neighborhood._id}>
                                <Form.Check
                                  type="checkbox"
                                  label={`${neighborhood.name} (${neighborhood.slug})`}
                                  checked={formData.assignedNeighborhoods.includes(neighborhood._id)}
                                  onChange={() => handleNeighborhoodToggle(neighborhood._id)}
                                />
                              </div>
                            ))}
                          </div>

                          {formData.assignedNeighborhoods.length > 0 && (
                            <div className="mt-3">
                              <label className="mainLabel">Default Neighborhood</label>
                              <Form.Select
                                value={formData.defaultNeighborhood}
                                onChange={(e) => handleChange('defaultNeighborhood', e.target.value)}
                              >
                                <option value="">Select default...</option>
                                {(neighborhoodsData?.data || [])
                                  .filter(n => formData.assignedNeighborhoods.includes(n._id))
                                  .map(neighborhood => (
                                    <option key={neighborhood._id} value={neighborhood._id}>
                                      {neighborhood.name}
                                    </option>
                                  ))}
                              </Form.Select>
                              <small className="text-muted">
                                This will be their starting neighborhood after sign-in.
                              </small>
                            </div>
                          )}
                        </>
                      )}

                      {formData.role === 'neighborhood_admin' && (
                        <>
                          <div className="section-divider">
                            <h5 className="section-title">Additional Permissions</h5>
                          </div>

                          <div className="permissions-container">
                            <div className="permission-item">
                              <Form.Check
                                type="checkbox"
                                label="Delete Users"
                                checked={formData.permissions.deleteUsers}
                                onChange={(e) => handleChange('permissions.deleteUsers', e.target.checked)}
                              />
                            </div>
                            <div className="permission-item">
                              <Form.Check
                                type="checkbox"
                                label="Manage Subscriptions"
                                checked={formData.permissions.manageSubscriptions}
                                onChange={(e) => handleChange('permissions.manageSubscriptions', e.target.checked)}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="admin-create-actions">
                      <CustomButton
                        type="submit"
                        variant="primaryButton"
                        loading={isLoading}
                      >
                        {isLoading ? 'Creating...' : 'Create & Send Invite'}
                      </CustomButton>
                      <CustomButton
                        variant="secondaryButton"
                        onClick={() => navigate('/admins')}
                      >
                        Cancel
                      </CustomButton>
                    </div>
                  </div>

                  <div className="col-xl-4">
                    <div className="admin-create-summary">
                      <p className="admin-create-summary-kicker">Invite Flow</p>
                      <h3 className="admin-create-summary-title">What happens next</h3>
                      <ul className="admin-create-summary-list">
                        <li>A temporary password is generated automatically.</li>
                        <li>The new admin gets an email invite through Resend with the portal link.</li>
                        <li>They sign in once and are routed straight into password setup.</li>
                      </ul>
                    </div>

                    <div className="admin-create-summary admin-create-summary-muted">
                      <p className="admin-create-summary-kicker">Access Snapshot</p>
                      <h3 className="admin-create-summary-title">
                        {formData.role === 'super_admin' ? 'Full system access' : 'Scoped neighborhood access'}
                      </h3>
                      <p className="secondaryText mb-0">
                        {formData.role === 'super_admin'
                          ? 'This admin will be able to work across every neighborhood in the console.'
                          : `${formData.assignedNeighborhoods.length} neighborhood${formData.assignedNeighborhoods.length === 1 ? '' : 's'} selected for this admin.`}
                      </p>
                    </div>
                  </div>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}

export default CreateAdmin
